import type { ColumnDef } from '@tanstack/react-table';
import cx from 'clsx';
import { dateToYMDHM } from 'common/date.ts';
import { MPOST, POST } from 'common/helpers.ts';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import useSWRMutation from 'swr/mutation';
import type { Role } from 'types/Role.ts';
import type { SupportRequest } from 'types/SupportRequest.ts';
import type { User } from 'types/User.ts';
import { Autocomplete } from 'ui/component/Autocomplete.tsx';
import { Button } from 'ui/component/Button.tsx';
import { IAdd, ISupportAgent } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Select } from 'ui/control/Select.tsx';
import axios from '#admin/axios.ts';
import { ConfirmDialog } from '#admin/component/ConfirmDialog.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { getTranslatedError } from '#admin/helpers.ts';
import { useDebounce } from '#admin/hook/useDebounce.ts';

const PAGE_SIZE = 50;

const getStatusClassName = (status: string) => {
  switch (status) {
    case 'accepted':
      return 'green';
    case 'revoked':
      return 'gray';
    default:
      return '';
  }
};

type GrantAccessDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type GrantFormValues = {
  operatorId: string;
  roleId: string;
  duration: 1 | 2 | 4 | 8;
};

const GRANT_DEFAULTS: GrantFormValues = { operatorId: '', roleId: '', duration: 1 };

const GrantAccessDialog: React.FC<GrantAccessDialogProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  // operatorSearch is transient UI state (the autocomplete query), not a submitted form value
  const [operatorSearch, setOperatorSearch] = React.useState('');
  const grantSwr = useSWRMutation('/support-request/grant', MPOST);
  const debouncedSearch = useDebounce(operatorSearch);

  const formMethods = useForm<GrantFormValues>({ mode: 'onChange', defaultValues: GRANT_DEFAULTS });
  const { control, handleSubmit, watch, reset, formState } = formMethods;

  // the dialog stays mounted between openings, so reset the form (and any stale error) on each open
  // biome-ignore lint/correctness/useExhaustiveDependencies: isOpen is the only trigger; reset/grantSwr.reset are stable
  React.useEffect(() => {
    if (isOpen) {
      reset(GRANT_DEFAULTS);
      setOperatorSearch('');
      grantSwr.reset();
    }
  }, [isOpen]);

  const { data: operators, isLoading: operatorsLoading } = useSWR<User[]>(
    debouncedSearch.length >= 1 ? ['/user/list', { is_group: false, search: debouncedSearch, limit: 10, sort: [['name', 'asc']] }] : null,
    POST,
  );
  const { data: roles } = useSWR<Role[]>(['/role/list', { sort: [['name', 'asc']] }], POST);

  const operatorSuggestions = (operators ?? []).map((u) => ({ key: u.id, label: `${u.name} (${u.email})` }));
  const roleOptions = React.useMemo(() => Object.fromEntries((roles ?? []).map((role) => [role.id, role.name])), [roles]);

  const onSubmit = async ({ operatorId, roleId, duration }: GrantFormValues) => {
    try {
      await grantSwr.trigger({ operator_id: operatorId, requested_role_id: roleId, access_duration_hours: duration });
    } catch {
      return; // error is captured by grantSwr.error and shown below
    }
    onSuccess();
  };

  const operatorId = watch('operatorId');
  const roleId = watch('roleId');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('supportAccess.grantTitle')}>
      <FormProvider {...formMethods}>
        <form className="flex min-w-[380px] flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="operatorId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                label={t('supportAccess.operator')}
                placeholder={t('supportAccess.selectOperator')}
                value={field.value || operatorSearch}
                onValueChange={(newValue) => {
                  if (operatorSuggestions.some((s) => s.key === newValue)) {
                    field.onChange(newValue);
                    setOperatorSearch('');
                  } else {
                    field.onChange('');
                    setOperatorSearch(newValue);
                  }
                }}
                suggestions={operatorSuggestions}
                loading={operatorsLoading}
              />
            )}
          />

          <Select name="roleId" label={t('supportAccess.role')} placeholder={t('supportAccess.selectRole')} options={roleOptions} registerOptions={{ required: true }} />

          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <p className="font-medium text-sm">{t('supportAccess.selectDuration')}</p>
                <div className="flex gap-3">
                  {([1, 2, 4, 8] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => field.onChange(d)}
                      className={cx(
                        'cursor-pointer rounded border px-4 py-2 font-medium text-sm transition-colors',
                        field.value === d ? 'border-blue bg-blue text-white' : 'border-gray-300 hover:border-blue',
                      )}
                    >
                      {t('supportAccess.hours', { count: d })}
                    </button>
                  ))}
                </div>
              </div>
            )}
          />

          {grantSwr.error && <p className="text-red-600 text-sm">{getTranslatedError(grantSwr.error, t) || t('supportAccess.grantError')}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" className="button-secondary w-max" onClick={onClose} disabled={formState.isSubmitting}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" className="w-max" disabled={formState.isSubmitting || !operatorId || !roleId} loading={formState.isSubmitting}>
              {t('supportAccess.grantAccess')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export const SupportRequestList: React.FC = () => {
  const { t } = useTranslation();
  const [grantDialogOpen, setGrantDialogOpen] = React.useState(false);
  const [revokeTarget, setRevokeTarget] = React.useState<string | null>(null);

  const swr = useSWRInfinite(getSWRKeyForPage('/support-request/list', PAGE_SIZE, {}, [{ id: 'created_at', desc: true }]), POST, { revalidateFirstPage: false });
  const { data: rowsCount, mutate: mutateCount } = useSWR<number>(['/support-request/count', {}], POST);

  const handleRevoke = async (id: string) => {
    await axios.delete(`/support-request/${id}`);
    void swr.mutate();
    void mutateCount();
  };

  const columns: ColumnDef<SupportRequest>[] = [
    {
      accessorKey: 'operator_name',
      header: t('supportAccess.operator'),
      meta: { truncate: true, className: 'items-center' },
    },
    {
      accessorKey: 'operator_email',
      header: t('labels.email'),
      meta: { truncate: true, className: 'items-center' },
    },
    {
      accessorKey: 'requested_role_name',
      header: t('supportAccess.requestedRole'),
      meta: { className: 'items-center' },
    },
    {
      accessorKey: 'status',
      header: t('labels.status'),
      meta: { className: 'items-center' },
      cell: (info) => {
        const status = info.getValue<string>();
        return <Tag className={cx('!p-2', getStatusClassName(status))}>{t(`supportAccess.status.${status}`)}</Tag>;
      },
    },
    {
      accessorKey: 'created_at',
      header: t('labels.creationDate'),
      meta: { className: 'items-center' },
      cell: (info) => dateToYMDHM(info.getValue<string>()),
    },
    {
      accessorKey: 'expires_at',
      header: t('supportAccess.expiresAt'),
      meta: { className: 'items-center' },
      cell: (info) => {
        const val = info.getValue<string | null>();
        return val ? dateToYMDHM(val) : '—';
      },
    },
    {
      id: 'actions',
      header: '',
      size: 120,
      meta: { hideFallback: true, className: 'items-center' },
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex gap-2">
            {row.status === 'accepted' && (
              <Button small className="button-danger w-max" onClick={() => setRevokeTarget(row.id)}>
                {t('actions.revoke')}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <main className="view-container flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-2xl">{t('supportAccess.title')}</h1>
        <Button className="w-max" onClick={() => setGrantDialogOpen(true)} LeftIcon={IAdd}>
          {t('supportAccess.grantAccess')}
        </Button>
      </div>

      <Table
        estimateSize={52}
        columns={columns}
        swr={swr}
        rowsCount={rowsCount}
        pagination={true}
        emptyState={
          <>
            <ISupportAgent size="48" className="text-gray-400" />
            <p className="mt-2 text-gray-500">{t('supportAccess.noGrants')}</p>
          </>
        }
      />

      <GrantAccessDialog
        isOpen={grantDialogOpen}
        onClose={() => setGrantDialogOpen(false)}
        onSuccess={() => {
          setGrantDialogOpen(false);
          void swr.mutate();
          void mutateCount();
        }}
      />

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title={t('supportAccess.revokeTitle')}
        message={t('supportAccess.revokeConfirmMessage')}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => {
          void handleRevoke(revokeTarget!);
          setRevokeTarget(null);
        }}
        confirmLabel={t('actions.revoke')}
        cancelLabel={t('actions.cancel')}
        variant="danger"
      />
    </main>
  );
};
